import {
  useQueries,
  useQuery,
  type QueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import type { ShortLinkTargetType } from '@pairy/sheard/types';

import { api } from '@/utils/api';
import { ApiErrorResponse, ApiResponse } from '@/types/api';

export interface ShortShareTokenRequest {
  targetType: ShortLinkTargetType;
  /** 지금 복사 버튼이 쓰는 hashId/slug 그대로 */
  hash: string;
}

/** 커스텀 링크를 설정할 수 없는 이유. 플랜 문제인지, 대상 자체가 불가인지. */
export type CustomizeDeniedReason = 'PLAN' | 'TARGET_TYPE';

export interface ShortShareTokenResponse {
  token: string;
  /**
   * 사용자가 정한 별칭. 미설정이면 null.
   *
   * 아래 세 필드는 백엔드가 나중에 붙인 것이라 optional 이다. 클라이언트가 먼저 배포돼도
   * 타입이 깨지지 않고, 필드가 없으면 호출부가 플랜으로 폴백한다.
   * 플랜과 무관하게 내려오므로 Free 로 내려온 사용자도 자기 주소를 보고 해제할 수 있다.
   */
  customSlug?: string | null;
  canCustomize?: boolean;
  customizeDeniedReason?: CustomizeDeniedReason | null;
}

/** `/s/` 뒤에 붙일 값. 커스텀 주소가 있으면 그것이 대표 URL 이다(기획서 §22). */
export const pickShareAlias = ({
  token,
  customSlug,
}: ShortShareTokenResponse) => customSlug ?? token;

export const issueShortShareToken = ({
  targetType,
  hash,
}: ShortShareTokenRequest) => {
  return api.post<ApiResponse<ShortShareTokenResponse>>(
    '/v2/share/short-link',
    {
      targetType,
      hash,
    },
  );
};

export const shortShareTokenQueryKey = (request: ShortShareTokenRequest) =>
  ['getShortShareToken', request] as const;

/** 대상이 없거나 권한이 없는 응답은 재시도해도 결과가 같다 */
const NON_RETRYABLE_STATUS = [400, 403, 404];

const retryShortShareToken = (failureCount: number, error: unknown) => {
  const status = (error as AxiosError<ApiErrorResponse>)?.response?.status ?? 0;

  return failureCount < 1 && !NON_RETRYABLE_STATUS.includes(status);
};

/**
 * 단축 링크 토큰을 발급받는다. 발급 API는 멱등이라 같은 대상은 항상 같은 토큰이 오므로
 * 캐시해두고 다시 부르지 않는다.
 *
 * mutation이 아니라 query인 이유: 클립보드 복사는 사용자 제스처와 같은 태스크에서
 * 동기적으로 일어나야 한다(Safari). 클릭 핸들러 안에서 await 하면 복사가 실패하므로
 * 렌더 시점에 미리 받아두고 클릭할 때는 값만 읽는다.
 */
const useShortShareToken = <
  TData = AxiosResponse<ApiResponse<ShortShareTokenResponse>>,
  TError = AxiosError<ApiErrorResponse>,
>(
  request: ShortShareTokenRequest,
  options?: UseQueryOptions<
    AxiosResponse<ApiResponse<ShortShareTokenResponse>>,
    TError,
    TData,
    ReturnType<typeof shortShareTokenQueryKey>
  >,
): UseQueryResult<TData, TError> => {
  return useQuery(
    shortShareTokenQueryKey(request),
    () => issueShortShareToken(request),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: Boolean(request.hash),
      retry: retryShortShareToken,
      ...options,
    },
  );
};

/**
 * 커스텀 링크 설정/해제 응답을 발급 캐시에 즉시 반영한다.
 *
 * invalidateQueries 가 아닌 이유: staleTime 이 Infinity 라 invalidate 는 곧바로 재발급을
 * 부르고, 무엇보다 복사 버튼은 렌더 시점에 URL 이 확정돼 있어야 한다(Safari 제약).
 * 설정/해제 API 가 이미 권위 있는 값을 돌려주므로 그대로 덮어쓰는 편이 맞다.
 */
export const patchShortShareTokenCache = (
  queryClient: QueryClient,
  request: ShortShareTokenRequest,
  patch: Partial<Pick<ShortShareTokenResponse, 'token' | 'customSlug'>>,
) => {
  queryClient.setQueryData<AxiosResponse<ApiResponse<ShortShareTokenResponse>>>(
    shortShareTokenQueryKey(request),
    (previous) =>
      previous && {
        ...previous,
        data: {
          ...previous.data,
          data: { ...previous.data.data, ...patch },
        },
      },
  );
};

/** 1:1 링크 목록처럼 여러 대상의 토큰이 한 번에 필요할 때 */
export const useShortShareTokens = (
  requests: ShortShareTokenRequest[],
  options?: { enabled?: boolean },
) => {
  return useQueries({
    queries: requests.map((request) => ({
      queryKey: shortShareTokenQueryKey(request),
      queryFn: () => issueShortShareToken(request),
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: (options?.enabled ?? true) && Boolean(request.hash),
      retry: retryShortShareToken,
      select: (data: AxiosResponse<ApiResponse<ShortShareTokenResponse>>) =>
        data.data.data.token,
    })),
  });
};

export default useShortShareToken;
